/**
 * @file HauliersManager.js
 * @version 3.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-01-19
 * @modified 2026-10-26
 * @description Manage hauliers locally (no Xero sync). Supports name/email editing and archiving.
 *
 *              3.2.0 (2026-10-26, Cam): "Supplier (for third-party haulage)" —
 *              link a haulier to the supplier contact it bills as (JAH & RH
 *              James → Jason James). A Third-Party Delivery order whose
 *              haulier is that supplier, with a PO-D, is then planned on the
 *              transport board with this haulier (services/
 *              thirdPartyBoardService). Greyed "Not ready" until
 *              migrations/transport_haulier_supplier_link.sql has run.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './HauliersManager.css';
import Input from './Input';
import SearchableDropdown from './SearchableDropdown';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { hasPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';

// What Clear Filters resets the search box and archived toggle to.
const CLEARED_FILTERS = { search: '', showArchived: false };

function HauliersManager({ user }) {
  const [hauliers, setHauliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Name editing state
  const [editingNameId, setEditingNameId] = useState(null);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Email editing state
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Phone editing state
  const [editingPhoneId, setEditingPhoneId] = useState(null);
  const [phoneValue, setPhoneValue] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [modalError, setModalError] = useState('');
  const [saving, setSaving] = useState(false);

  // History modal state
  const [showHistory, setShowHistory] = useState(false);
  const [historyHaulier, setHistoryHaulier] = useState(null);

  // Supplier link (third-party haulage): who each haulier bills as.
  const [suppliers, setSuppliers] = useState([]);
  const [supplierLink, setSupplierLink] = useState({ ready: true, reason: null });
  const [savingSupplierId, setSavingSupplierId] = useState(null);

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });

  // Slideout buttons
  useSlideOutButton([showAddModal, saving]);

  const fetchHauliers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers?includeArchived=${showArchived}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setHauliers(data.hauliers);
        setSupplierLink({ ready: data.supplier_link_ready !== false, reason: data.supplier_link_reason || null });
      }
    } catch (err) {
      console.error('Error fetching hauliers:', err);
      setAlertDialog({ isOpen: true, message: 'Failed to load hauliers', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchHauliers(); }, [fetchHauliers]);

  // Active supplier contacts (SUPPLIER and BOTH), as the order's Third-Party
  // Haulier picker lists them.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setSuppliers(d.contacts || []); })
      .catch(err => console.error('Error loading suppliers:', err));
    return () => { cancelled = true; };
  }, []);

  const supplierOptions = [
    { value: '', label: '— No supplier —' },
    ...suppliers.map(sup => ({
      value: String(sup.id),
      label: sup.company_name,
      badge: sup.account_no || undefined,
      searchText: `${sup.company_name} ${sup.account_no || ''} ${sup.email || ''}`
    }))
  ];

  const filteredHauliers = hauliers.filter(h =>
    matchesAllTerms([h.name, h.email, h.supplier_name], searchTerm)
  );

  const ctxMenu = useTableContextMenu({
    rows: filteredHauliers,
    getRowId: (h) => h.id
  });

  // Permission gate — placed AFTER all hooks to satisfy react-hooks/rules-of-hooks.
  // The fetchHauliers useEffect will fire and 403 for non-permitted users, which
  // is harmless: the component still renders AccessDenied below.
  if (!hasPermission(user, 'transport_planning')) {
    return <AccessDenied permission="Transport Planning" />;
  }

  // ── Supplier link (third-party haulage) ───────────────────────
  const saveSupplierLink = async (haulier, value) => {
    const next = value ? Number(value) : null;
    if ((haulier.supplier_account_id ? Number(haulier.supplier_account_id) : null) === next) return;
    setSavingSupplierId(haulier.id);
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers/${haulier.id}/supplier`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplierAccountId: next })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to link the supplier', type: 'error' });
        return;
      }
      setHauliers(prev => prev.map(h => (h.id === haulier.id
        ? { ...h, supplier_account_id: data.haulier?.supplier_account_id ?? next, supplier_name: data.haulier?.supplier_name || null }
        : h)));
      // Orders already on that supplier take the haulier straight away — say which.
      const moved = (data.synced || []).filter(o => o.changed > 0);
      if (next && moved.length) {
        setAlertDialog({
          isOpen: true, type: 'success',
          message: `${haulier.name} set on ${moved.length} third-party order${moved.length === 1 ? '' : 's'} with a PO-D — now on the transport board.`
        });
      }
    } catch (err) {
      console.error('Error linking supplier:', err);
      setAlertDialog({ isOpen: true, message: 'Failed to link the supplier', type: 'error' });
    } finally {
      setSavingSupplierId(null);
    }
  };

  // ── Name editing ──────────────────────────────────────────────
  const startEditName = (haulier) => {
    if (editingEmailId) return; // Don't allow both at once
    setEditingNameId(haulier.id);
    setNameValue(haulier.name);
  };

  const cancelEditName = () => { setEditingNameId(null); setNameValue(''); };

  const saveNameEdit = async (haulierId) => {
    const trimmed = nameValue.trim();
    if (!trimmed) return;
    if (trimmed === hauliers.find(h => h.id === haulierId)?.name) {
      cancelEditName();
      return;
    }
    setSavingName(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers/${haulierId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (data.success) {
        setHauliers(prev => prev.map(h => h.id === haulierId ? { ...h, name: data.haulier.name } : h));
        setEditingNameId(null);
        setNameValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update name', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update name', type: 'error' });
    } finally {
      setSavingName(false);
    }
  };

  // ── Email editing ─────────────────────────────────────────────
  const startEditEmail = (haulier) => {
    if (editingNameId) return;
    setEditingEmailId(haulier.id);
    setEmailValue(haulier.email || '');
  };

  const cancelEditEmail = () => { setEditingEmailId(null); setEmailValue(''); };

  const saveEmailEdit = async (haulierId) => {
    setSavingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers/${haulierId}/email`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue.trim() || null })
      });
      const data = await res.json();
      if (data.success) {
        setHauliers(prev => prev.map(h => h.id === haulierId ? { ...h, email: data.haulier.email } : h));
        setEditingEmailId(null);
        setEmailValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update email', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update email', type: 'error' });
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Phone editing ────────────────────────────────────────────
  const startEditPhone = (haulier) => {
    if (editingNameId || editingEmailId) return;
    setEditingPhoneId(haulier.id);
    setPhoneValue(haulier.phone || '');
  };

  const cancelEditPhone = () => { setEditingPhoneId(null); setPhoneValue(''); };

  const savePhoneEdit = async (haulierId) => {
    setSavingPhone(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers/${haulierId}/phone`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneValue.trim() || null })
      });
      const data = await res.json();
      if (data.success) {
        setHauliers(prev => prev.map(h => h.id === haulierId ? { ...h, phone: data.haulier.phone } : h));
        setEditingPhoneId(null);
        setPhoneValue('');
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to update phone', type: 'error' });
      }
    } catch (err) {
      setAlertDialog({ isOpen: true, message: 'Failed to update phone', type: 'error' });
    } finally {
      setSavingPhone(false);
    }
  };

  // ── Archive ───────────────────────────────────────────────────
  const handleArchive = (haulier) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Archive Haulier',
      message: `Archive "${haulier.name}"? It will no longer appear in transport planning or PO dropdowns.`,
      type: 'warning',
      confirmText: 'Archive',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/transport/hauliers/${haulier.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          const data = await res.json();
          if (data.success) {
            setHauliers(prev => prev.map(h => h.id === haulier.id ? { ...h, status: 'ARCHIVED' } : h));
            if (!showArchived) setHauliers(prev => prev.filter(h => h.id !== haulier.id));
          } else {
            setAlertDialog({ isOpen: true, message: data.error || 'Failed to archive haulier', type: 'error' });
          }
        } catch (err) {
          setAlertDialog({ isOpen: true, message: 'Failed to archive haulier', type: 'error' });
        }
      }
    });
  };

  // ── Add haulier ───────────────────────────────────────────────
  const handleOpenAdd = () => {
    setNewName('');
    setNewEmail('');
    setModalError('');
    setShowAddModal(true);
  };

  const handleSaveNew = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { setModalError('Haulier name is required'); return; }
    if (trimmed.length > 100) { setModalError('Name must be 100 characters or less'); return; }

    setSaving(true);
    setModalError('');
    try {
      const res = await fetch(`${API_URL}/api/transport/hauliers`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed, email: newEmail.trim() || undefined })
      });
      const data = await res.json();
      if (data.success) {
        setHauliers(prev => [...prev, data.haulier].sort((a, b) => a.name.localeCompare(b.name)));
        setShowAddModal(false);
      } else {
        setModalError(data.error || 'Failed to create haulier');
      }
    } catch (err) {
      setModalError('Failed to create haulier. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const buildRowActions = (haulier) => [
    { key: 'email', icon: 'mail', label: 'Edit email', title: 'Edit email', onClick: () => startEditEmail(haulier) },
    { key: 'history', icon: 'history', label: 'View history', title: 'View history', onClick: () => { setHistoryHaulier(haulier); setShowHistory(true); } },
    ...(haulier.status === 'ACTIVE' ? [
      { key: 'archive', icon: 'archive', label: 'Archive haulier', title: 'Archive haulier', onClick: () => handleArchive(haulier) },
    ] : []),
  ];

  // Any header filter applied lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(
    { search: searchTerm, showArchived }, CLEARED_FILTERS, { labels: { showArchived: 'Archived' } }
  );
  const filtersApplied = headerFilters.count > 0;

  return (
    <div className="search-layout hauliers-manager" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage Hauliers</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Manage hauliers and email addresses for transport planning paperwork.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => { setSearchTerm(CLEARED_FILTERS.search); setShowArchived(CLEARED_FILTERS.showArchived); }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleOpenAdd}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Haulier</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr auto' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['name', 'email', 'linked supplier']} />
            </label>
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-hauliers"
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <label htmlFor="show-archived-hauliers"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : filteredHauliers.length === 0 ? (
            <div className="no-results">
              <span className="material-symbols-rounded">local_shipping</span>
              <p>{searchTerm ? 'No matching hauliers found' : 'No hauliers available. Click "Add Haulier" to create one.'}</p>
            </div>
          ) : (
            <table className="search-results-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email Address</th>
                  <th>Phone</th>
                  <th className="hm-supplier-col" title="The supplier contact this haulier bills as. A Third-Party Delivery order with that haulier supplier and a PO-D is planned on the transport board with this haulier.">
                    Supplier (for third-party haulage)
                  </th>
                  <th className="status-col">Status</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredHauliers.map(haulier => (
                  <tr
                    key={haulier.id}
                    className={haulier.status !== 'ACTIVE' ? 'hm-archived-row' : ''}
                    onContextMenu={ctxMenu.onRowContextMenu(haulier)}
                  >
                    {/* Name cell */}
                    <td className="hm-name-cell">
                      {editingNameId === haulier.id ? (
                        <div className="hm-inline-edit">
                          <input
                            type="text"
                            className="hm-edit-input"
                            value={nameValue}
                            onChange={(e) => setNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveNameEdit(haulier.id);
                              if (e.key === 'Escape') cancelEditName();
                            }}
                            maxLength={100}
                            autoFocus
                            disabled={savingName}
                          />
                          <button className="btn-icon hm-btn-save" onClick={() => saveNameEdit(haulier.id)} disabled={savingName} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditName} disabled={savingName} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span className="hm-name-display" onClick={() => startEditName(haulier)} title="Click to edit name">
                          <strong>{haulier.name}</strong>
                        </span>
                      )}
                    </td>

                    {/* Email cell */}
                    <td className="hm-email-cell">
                      {editingEmailId === haulier.id ? (
                        <div className="hm-inline-edit">
                          <input
                            type="email"
                            className="hm-edit-input"
                            value={emailValue}
                            onChange={(e) => setEmailValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveEmailEdit(haulier.id);
                              if (e.key === 'Escape') cancelEditEmail();
                            }}
                            placeholder="Enter email address"
                            autoFocus
                            disabled={savingEmail}
                          />
                          <button className="btn-icon hm-btn-save" onClick={() => saveEmailEdit(haulier.id)} disabled={savingEmail} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditEmail} disabled={savingEmail} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`hm-email-display ${!haulier.email ? 'hm-empty' : ''}`}
                          onClick={() => startEditEmail(haulier)}
                          title="Click to edit email"
                        >
                          {haulier.email || 'Click to add email'}
                        </span>
                      )}
                    </td>

                    {/* Phone cell */}
                    <td className="hm-phone-cell">
                      {editingPhoneId === haulier.id ? (
                        <div className="hm-inline-edit">
                          <input
                            type="tel"
                            className="hm-edit-input"
                            value={phoneValue}
                            onChange={(e) => setPhoneValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') savePhoneEdit(haulier.id);
                              if (e.key === 'Escape') cancelEditPhone();
                            }}
                            placeholder="Enter phone number"
                            autoFocus
                            disabled={savingPhone}
                          />
                          <button className="btn-icon hm-btn-save" onClick={() => savePhoneEdit(haulier.id)} disabled={savingPhone} title="Save">
                            <span className="material-symbols-rounded">check</span>
                          </button>
                          <button className="btn-icon" onClick={cancelEditPhone} disabled={savingPhone} title="Cancel">
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      ) : (
                        <span
                          className={`hm-email-display ${!haulier.phone ? 'hm-empty' : ''}`}
                          onClick={() => startEditPhone(haulier)}
                          title="Click to edit phone"
                        >
                          {haulier.phone || 'Click to add phone'}
                        </span>
                      )}
                    </td>

                    {/* Supplier link (third-party haulage) */}
                    <td className="hm-supplier-cell">
                      <div title={!supplierLink.ready ? supplierLink.reason
                        : haulier.status !== 'ACTIVE' ? 'Archived — restore the haulier to link a supplier'
                          : 'The supplier contact this haulier bills as for third-party haulage'}>
                        <SearchableDropdown
                          compact
                          escapeOverflow
                          value={haulier.supplier_account_id ? String(haulier.supplier_account_id) : ''}
                          onChange={(value) => saveSupplierLink(haulier, value)}
                          options={supplierOptions.some(o => o.value === String(haulier.supplier_account_id || ''))
                            ? supplierOptions
                            : [...supplierOptions, { value: String(haulier.supplier_account_id), label: haulier.supplier_name || `Contact #${haulier.supplier_account_id}` }]}
                          placeholder={supplierLink.ready ? '— No supplier —' : 'Not ready'}
                          searchPlaceholder="Type a supplier name..."
                          disabled={!supplierLink.ready || haulier.status !== 'ACTIVE' || savingSupplierId === haulier.id}
                        />
                      </div>
                    </td>

                    <td className="status-col">
                      <span className={`status-badge ${haulier.status === 'ACTIVE' ? 'status-active' : 'status-archived'}`}>
                        {haulier.status}
                      </span>
                    </td>

                    <td className="actions-col">
                      {editingNameId !== haulier.id && editingEmailId !== haulier.id && editingPhoneId !== haulier.id && (
                        <RowActionsMenu actions={buildRowActions(haulier)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add Haulier Modal */}
      {showAddModal && createPortal(
        <div className="modal-overlay" onClick={() => !saving && setShowAddModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Haulier</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)} disabled={saving}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: "0 var(--spacing-xl) var(--spacing-xl)" }}>
              {modalError && (
                <div style={{
                  padding: 'var(--spacing-sm)',
                  background: 'rgba(var(--color-error-rgb), 0.1)',
                  border: '1px solid var(--color-error)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-error)',
                  marginBottom: 'var(--spacing-md)',
                  fontSize: 'var(--font-sm)'
                }}>{modalError}</div>
              )}
              <div className="form-group">
                <label>Haulier Name <span style={{ color: 'var(--color-error)' }}>*</span></label>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter haulier name"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSaveNew(); }}
                />
              </div>
              <div className="form-group">
                <label>Email Address <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>(optional)</span></label>
                <input
                  type="email"
                  className="form-control"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter email for paperwork dispatch"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !saving) handleSaveNew(); }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button data-read-safe className="btn-round btn-danger" onClick={() => setShowAddModal(false)} disabled={saving}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Cancel</span>
              </button>
              <button className="btn-slideout" onClick={handleSaveNew} disabled={saving || !newName.trim()}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">{saving ? 'hourglass_top' : 'save'}</span>
                </div>
                <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {showHistory && historyHaulier && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => { setShowHistory(false); setHistoryHaulier(null); }}
          entityType="haulier"
          entityId={historyHaulier.id}
          entityIdentifier={historyHaulier.name}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add haulier', onClick: handleOpenAdd },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: fetchHauliers },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived',
            active: showArchived,
            onClick: () => setShowArchived(v => !v)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: () => { setSearchTerm(''); setShowArchived(false); }
          }
        ]}
      />
    </div>
  );
}

export default HauliersManager;
